import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const THEME_STORAGE_KEY = 'shefa-theme-pref';

export function getSystemTheme() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function readStoredPreference() {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch (_) {
    /* ignore */
  }
  return 'light';
}

export function resolveEffectiveTheme(preference) {
  if (preference === 'system') return getSystemTheme();
  return preference;
}

export function applyDomTheme(effective) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-bs-theme', effective);
  document.documentElement.setAttribute('data-shefa-theme', effective);
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() =>
    typeof window !== 'undefined' ? readStoredPreference() : 'light'
  );
  const [systemTick, setSystemTick] = useState(0);

  const effectiveTheme = useMemo(
    () => resolveEffectiveTheme(preference),
    [preference, systemTick]
  );

  useEffect(() => {
    applyDomTheme(effectiveTheme);
  }, [effectiveTheme]);

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch (_) {
      /* ignore */
    }
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setSystemTick((n) => n + 1);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setPreference = useCallback((next) => {
    if (next === 'light' || next === 'dark' || next === 'system') {
      setPreferenceState(next);
    }
  }, []);

  const cycleTheme = useCallback(() => {
    setPreferenceState((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  }, []);

  const value = useMemo(
    () => ({
      preference,
      effectiveTheme,
      setPreference,
      cycleTheme,
      isLight: effectiveTheme === 'light',
    }),
    [preference, effectiveTheme, setPreference, cycleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

/** Chart metric colors: vivid (dark UI) vs pastel (light UI), same hue family */
export function getClimateChartPalette(isLight) {
  if (isLight) {
    return {
      temperature: '#fb923c',
      humidity: '#38bdf8',
      co2: '#4ade80',
      vpd: '#c4b5fd',
      nightZone: 'rgba(15, 23, 42, 0.07)',
      tick: '#64748b',
      tooltipBg: 'rgba(15, 23, 42, 0.9)',
    };
  }
  return {
    temperature: '#f97316',
    humidity: '#38bdf8',
    co2: '#22c55e',
    vpd: '#c084fc',
    nightZone: 'rgba(148, 163, 184, 0.16)',
    tick: '#94a3b8',
    tooltipBg: 'rgba(17, 24, 39, 0.92)',
  };
}

/** Water history charts — pastel in light theme */
export function getWaterChartTheme(isLight) {
  if (isLight) {
    return {
      slate: '#64748b',
      grid: 'rgba(15, 23, 42, 0.07)',
      datasets: {
        ph: { border: 'rgb(14, 165, 233)', fill: 'rgba(14, 165, 233, 0.14)' },
        ec: { border: 'rgb(22, 163, 74)', fill: 'rgba(22, 163, 74, 0.12)' },
        orp: { border: 'rgb(217, 119, 6)', fill: 'rgba(217, 119, 6, 0.12)' },
        temp: { border: 'rgb(234, 88, 12)', fill: 'rgba(234, 88, 12, 0.12)' },
        volume: { border: 'rgb(37, 99, 235)', fill: 'rgba(37, 99, 235, 0.12)' },
      },
    };
  }
  return {
    slate: '#94a3b8',
    grid: 'rgba(255, 255, 255, 0.06)',
    datasets: {
      ph: { border: 'rgb(56, 189, 248)', fill: 'rgba(56, 189, 248, 0.22)' },
      ec: { border: 'rgb(34, 197, 94)', fill: 'rgba(34, 197, 94, 0.2)' },
      orp: { border: 'rgb(251, 191, 36)', fill: 'rgba(251, 191, 36, 0.18)' },
      temp: { border: 'rgb(249, 115, 22)', fill: 'rgba(249, 115, 22, 0.2)' },
      volume: { border: 'rgb(96, 165, 250)', fill: 'rgba(96, 165, 250, 0.22)' },
    },
  };
}
